import { useEffect } from "react";

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import SEO from "components/SEO";

// Material Kit 2 React examples
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import DefaultFooter from "examples/Footers/DefaultFooter";

// Data & config
import propertyData from "pages/Home/sections/data/propertyData";
import { rentalTerms } from "pages/CarRentals/sections/data/carRentalData";
import { promo } from "config/promo";

// Tracking
import { trackMetaEvent } from "utils/metaEvents";

// Images
import poolHero from "assets/images/property/pool.webp";
import vanImage from "assets/images/cars/van_front.webp";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

const included = [
  {
    icon: "airport_shuttle",
    title: "The brand-new 2027 Hyundai Staria",
    text: "Seats 9 with room to spare, twin-turbo diesel and a large touchscreen with Apple CarPlay & Android Auto \u2014 yours for the length of your stay.",
  },
  {
    icon: "event_available",
    title: "Any September or October stay",
    text: `Book any stay checking in during ${promo.windowLabel} and the van is automatically included \u2014 no code needed.`,
  },
  {
    icon: "savings",
    title: "On top of booking-direct savings",
    text: "You already save 15\u201320% vs Airbnb with no booking fees. The free van is an extra perk for filling these dates.",
  },
];

const steps = [
  {
    icon: "event",
    title: "Pick your dates",
    text: `Choose a check-in in ${promo.windowLabel} on the booking page.`,
  },
  {
    icon: "verified",
    title: "Book direct",
    text: "Send your request and reserve with a deposit \u2014 we confirm within 24 hours.",
  },
  {
    icon: "directions_car",
    title: "The van is waiting",
    text: "Arrive to find the 2027 Staria ready for you at the property for your whole stay.",
  },
];

// Reuse the exact "Credit card & security hold" copy from the car-rentals terms
// so it stays in sync in one place.
const securityHold = rentalTerms.find((term) => term.icon === "credit_card");

function SeptemberOffer() {
  useEffect(() => {
    trackMetaEvent("ViewContent");
  }, []);

  return (
    <>
      <SEO
        title="Free 2027 Van with September & October Stays"
        description="Book a September or October stay at Stalley Beach House and get our brand-new 2027 Hyundai Staria van free for your whole trip. Van only — airport transfers and the RAV4 are separate."
        path={promo.ctaPath}
        imageAlt="Brand-new 2027 Hyundai Staria van, free with September & October stays"
        noindex
      />
      <DefaultNavbar routes={routes} sticky />

      {/* Hero */}
      <MKBox
        minHeight="70vh"
        width="100%"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.dark.main, 0.5),
              rgba(gradients.dark.state, 0.7)
            )}, url(${poolHero})`,
          backgroundSize: "cover",
          backgroundPosition: "50% 60%",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid container justifyContent="center">
            <Grid item xs={12} md={10} lg={8} sx={{ textAlign: "center", mx: "auto" }}>
              <MKBox
                display="inline-flex"
                alignItems="center"
                gap={0.8}
                px={2}
                py={0.8}
                mb={2}
                borderRadius="lg"
                sx={{ backgroundColor: "warning.main", boxShadow: "0 4px 14px rgba(0,0,0,0.25)" }}
              >
                <Icon sx={{ color: "#fff", fontSize: "1.1rem !important" }}>local_offer</Icon>
                <MKTypography variant="button" sx={{ color: "#fff", fontWeight: 700 }}>
                  Limited-time &middot; {promo.windowLabel}
                </MKTypography>
              </MKBox>
              <MKTypography
                variant="h1"
                color="white"
                mb={2}
                sx={{ textShadow: "0 3px 20px rgba(0,0,0,0.45)" }}
              >
                {promo.headline}
              </MKTypography>
              <MKTypography variant="body1" color="white" opacity={0.9} mb={1}>
                Move your whole group around the coast in comfort &mdash; the brand-new 2027 Hyundai
                Staria van is included free with every stay this September and October.
              </MKTypography>
              <MKTypography variant="button" color="white" opacity={0.8} fontWeight="regular">
                Van rental only. Airport transfers &amp; the RAV4 are separate paid add-ons.
              </MKTypography>
              <MKBox mt={3}>
                <MKButton
                  variant="gradient"
                  color="info"
                  size="large"
                  component="a"
                  href="/booking"
                >
                  <Icon sx={{ mr: 1 }}>calendar_month</Icon>
                  Check September &amp; October availability
                </MKButton>
              </MKBox>
            </Grid>
          </Grid>
        </Container>
      </MKBox>

      <Card
        sx={{
          p: { xs: 2, md: 3 },
          mx: { xs: 2, lg: 3 },
          mt: -6,
          mb: 4,
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        {/* Here's the deal */}
        <MKBox component="section" py={6}>
          <Container>
            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} md={6}>
                <MKBox
                  component="img"
                  src={vanImage}
                  alt="Brand-new 2027 Hyundai Staria van included free with September and October stays"
                  width="100%"
                  borderRadius="lg"
                  sx={{ boxShadow: ({ boxShadows: { lg } }) => lg, display: "block" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <MKTypography variant="h3" mb={1}>
                  The van&apos;s on us
                </MKTypography>
                <MKTypography variant="body2" color="text" mb={3}>
                  We have a few open weeks this September and October, so we&apos;re throwing in our
                  newest vehicle to sweeten the deal. Book any qualifying stay and the 2027 Staria
                  is yours for the whole trip.
                </MKTypography>
                {included.map((item) => (
                  <MKBox key={item.title} display="flex" gap={1.5} mb={2}>
                    <MKBox
                      display="inline-flex"
                      alignItems="center"
                      justifyContent="center"
                      sx={{
                        minWidth: 44,
                        height: 44,
                        borderRadius: "50%",
                        backgroundColor: "info.main",
                      }}
                    >
                      <Icon sx={{ color: "#fff" }}>{item.icon}</Icon>
                    </MKBox>
                    <MKBox>
                      <MKTypography variant="h6">{item.title}</MKTypography>
                      <MKTypography variant="body2" color="text">
                        {item.text}
                      </MKTypography>
                    </MKBox>
                  </MKBox>
                ))}
              </Grid>
            </Grid>
          </Container>
        </MKBox>

        {/* What's separate */}
        <MKBox component="section" pb={6}>
          <Container>
            <MKBox
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              gap={2}
              p={{ xs: 3, md: 3.5 }}
              borderRadius="lg"
              sx={({ palette: { warning }, functions: { rgba } }) => ({
                backgroundColor: rgba(warning.main, 0.1),
                border: "1px solid",
                borderColor: rgba(warning.main, 0.3),
              })}
            >
              <Icon sx={{ color: "warning.main", fontSize: "2rem !important" }}>info</Icon>
              <MKBox flex={1}>
                <MKTypography variant="h6" mb={0.5}>
                  What&apos;s not included
                </MKTypography>
                <MKTypography variant="body2" color="text">
                  This offer covers the van only. Our free two-way airport transportation we usually
                  offer with the rentals isn&apos;t included unless you rent our Toyota RAV4
                  together with the van. See the{" "}
                  <MKTypography
                    component="a"
                    href="/car-rentals"
                    variant="body2"
                    fontWeight="bold"
                    color="info"
                    sx={{ textDecoration: "underline" }}
                  >
                    car rentals page
                  </MKTypography>{" "}
                  for details. Otherwise we&apos;re glad to help arrange transportation for your
                  group.
                </MKTypography>
              </MKBox>
            </MKBox>
          </Container>
        </MKBox>

        {/* Credit card & security hold (still applies to the free van) */}
        {securityHold && (
          <MKBox component="section" pb={6}>
            <Container>
              <MKTypography variant="body2" color="text" mb={2}>
                Good to know: the van itself is free, but the standard rental security hold still
                applies &mdash; exactly the same as with any of our vehicles.
              </MKTypography>
              <MKBox
                display="flex"
                flexDirection={{ xs: "column", sm: "row" }}
                alignItems="flex-start"
                gap={2}
                p={{ xs: 3, md: 3.5 }}
                borderRadius="lg"
                bgColor="grey-100"
              >
                <MKBox
                  display="inline-flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{
                    minWidth: 44,
                    height: 44,
                    borderRadius: "50%",
                    backgroundColor: "info.main",
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ color: "#fff" }}>credit_card</Icon>
                </MKBox>
                <MKBox flex={1}>
                  <MKTypography variant="h6" mb={0.5}>
                    {securityHold.title}
                  </MKTypography>
                  <MKTypography variant="body2" color="text">
                    {securityHold.body}
                  </MKTypography>
                </MKBox>
              </MKBox>
            </Container>
          </MKBox>
        )}

        {/* How to claim it */}
        <MKBox component="section" py={2}>
          <Container>
            <MKBox bgColor="grey-100" borderRadius="xl" py={5} px={{ xs: 2, md: 4 }}>
              <MKBox textAlign="center">
                <MKTypography variant="h3" mb={1}>
                  How to claim it
                </MKTypography>
                <MKTypography variant="body2" color="text" mb={4}>
                  Three simple steps &mdash; the van is added automatically for qualifying dates.
                </MKTypography>
              </MKBox>
              <Grid container spacing={3}>
                {steps.map((step, index) => (
                  <Grid item xs={12} md={4} key={step.title}>
                    <MKBox textAlign="center" px={2}>
                      <MKBox
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        mb={2}
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: "50%",
                          backgroundColor: "info.main",
                        }}
                      >
                        <Icon sx={{ color: "#fff", fontSize: "1.8rem !important" }}>
                          {step.icon}
                        </Icon>
                      </MKBox>
                      <MKTypography variant="h6" mb={1}>
                        {index + 1}. {step.title}
                      </MKTypography>
                      <MKTypography variant="body2" color="text">
                        {step.text}
                      </MKTypography>
                    </MKBox>
                  </Grid>
                ))}
              </Grid>
            </MKBox>
          </Container>
        </MKBox>

        {/* Fine print */}
        <MKBox component="section" py={4}>
          <Container>
            <MKTypography variant="caption" color="text" sx={{ fontStyle: "italic" }}>
              {promo.terms} Subject to availability; standard minimum-stay and booking terms apply.
              Offer for {propertyData.name} direct bookings only and may end at any time.
            </MKTypography>
          </Container>
        </MKBox>

        {/* Final CTA */}
        <MKBox component="section" pb={2}>
          <Container>
            <MKBox
              variant="gradient"
              bgColor="info"
              borderRadius="xl"
              textAlign="center"
              p={{ xs: 4, md: 6 }}
            >
              <MKTypography variant="h3" color="white" mb={1}>
                Ready to lock in your dates?
              </MKTypography>
              <MKTypography variant="body2" color="white" opacity={0.9} mb={3}>
                Check the calendar for September &amp; October and book direct to claim your free
                van.
              </MKTypography>
              <MKButton variant="gradient" color="light" size="large" component="a" href="/booking">
                <Icon sx={{ mr: 1 }}>calendar_month</Icon>
                Check availability
              </MKButton>
            </MKBox>
          </Container>
        </MKBox>
      </Card>

      <MKBox pt={6} px={1} mt={6}>
        <DefaultFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default SeptemberOffer;
